type DateDiffUnit = 'years' | 'months' | 'weeks' | 'days';

type TimeDiffUnit = 'seconds' | 'minutes' | 'hours';

type DateDiffDaysOfWeek =
	| 'all'
	| 'monday'
	| 'tuesday'
	| 'wednesday'
	| 'thursday'
	| 'friday'
	| 'saturday'
	| 'sunday'
	| 'weekdays'
	| 'weekends';

export type DateGroupRule =
	| { type: 'date'; ts: number }
	| { type: 'wildcard'; y: '*' | number; m: '*' | number; d: '*' | number };

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = MINUTE_SECONDS * 60;
const DAY_SECONDS = HOUR_SECONDS * 24;

interface DateDiffOpts {
	unit: DateDiffUnit;
	includeStart: boolean;
	includeEnd: boolean;
	exclude: (string | number)[] | string | number;
	daysOfWeek: DateDiffDaysOfWeek | DateDiffDaysOfWeek[];
	abs: boolean;
	tz: string;
}

interface TimeDiffOpts {
	unit: TimeDiffUnit;
	abs: boolean;
	tz: string;
	dstAware: boolean;
}

interface DateTimeOpts {
	tz: string;
}

interface ConvertTzOpts {
	fromTz: string;
}

const strings = window.gp_advanced_calculations_strings;

/**
 * Cache of `Intl.DateTimeFormat` instances keyed by timezone string. Formatters
 * are immutable and the same `(year, month, day, hour, minute, second)`-emitting
 * configuration is always used, so a single formatter per tz is sufficient for
 * the lifetime of the page.
 */
const tzFormatterCache = new Map<string, Intl.DateTimeFormat>();

function buildTzFormatter(tz: string): Intl.DateTimeFormat | null {
	try {
		return new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hour12: false,
		});
	} catch (e) {
		return null;
	}
}

interface DateParts {
	year: number;
	month: number; // 1-12
	day: number;
	hour: number; // 0-23
	minute: number; // 0-59
	second: number; // 0-59
}

/**
 * Extracts calendar date parts (year, month, day) from a Unix timestamp in the
 * specified timezone.
 *
 * This is the tz-aware replacement for `Date.prototype.getMonth/getFullYear/
 * getDate`, which silently uses the browser's local timezone. Using it keeps
 * `datediff()`'s calendar math (`months`, `years`) deterministic and
 * independent of where the visitor's browser lives.
 *
 * If `tz` is invalid (e.g. a field reference yields a non-IANA string),
 * falls back to UTC with a console warning.
 *
 * @param ts Unix timestamp in seconds.
 * @param tz IANA timezone string or `UTC±HH:MM` offset.
 */
function getDatePartsInTz(ts: number, tz: string): DateParts {
	let format = tzFormatterCache.get(tz);

	if (!format) {
		format = buildTzFormatter(tz) ?? undefined;

		if (!format) {
			// eslint-disable-next-line no-console
			console.warn(
				`[GPAC] datediff: invalid timezone "${tz}", falling back to UTC`
			);
			format = buildTzFormatter('UTC')!;
		}

		tzFormatterCache.set(tz, format);
	}

	const parts = format.formatToParts(new Date(ts * 1000));

	const result: DateParts = {
		year: 0,
		month: 0,
		day: 0,
		hour: 0,
		minute: 0,
		second: 0,
	};
	for (const p of parts) {
		switch (p.type) {
			case 'year':
				result.year = parseInt(p.value, 10);
				break;
			case 'month':
				result.month = parseInt(p.value, 10);
				break;
			case 'day':
				result.day = parseInt(p.value, 10);
				break;
			case 'hour':
				// Intl with hour12:false can emit "24" for midnight; normalize.
				result.hour = parseInt(p.value, 10) % 24;
				break;
			case 'minute':
				result.minute = parseInt(p.value, 10);
				break;
			case 'second':
				result.second = parseInt(p.value, 10);
				break;
		}
	}

	return result;
}

/** Map of day name strings to JS day-of-week numbers (0=Sunday..6=Saturday). */
const DAY_NAME_MAP: Record<string, number> = {
	sunday: 0,
	monday: 1,
	tuesday: 2,
	wednesday: 3,
	thursday: 4,
	friday: 5,
	saturday: 6,
};

/**
 * Resolves a `daysOfWeek` option value to a Set of JS day numbers (0=Sun..6=Sat).
 *
 * Accepts a single value or an array. Returns the full set (all 7 days) if:
 * - The input is `'all'`
 * - All elements are invalid (fail-open)
 *
 * Case-insensitive matching.
 * @param daysOfWeek
 */
function resolveDaysOfWeek(
	daysOfWeek: DateDiffDaysOfWeek | DateDiffDaysOfWeek[]
): Set<number> {
	const ALL_DAYS = new Set([0, 1, 2, 3, 4, 5, 6]);
	const input = Array.isArray(daysOfWeek) ? daysOfWeek : [daysOfWeek];
	const result = new Set<number>();

	for (const val of input) {
		const lower = String(val).toLowerCase();

		if (lower === 'all') {
			return ALL_DAYS;
		}

		if (lower === 'weekdays') {
			[1, 2, 3, 4, 5].forEach((d) => result.add(d));
			continue;
		}

		if (lower === 'weekends') {
			[0, 6].forEach((d) => result.add(d));
			continue;
		}

		if (lower in DAY_NAME_MAP) {
			result.add(DAY_NAME_MAP[lower]);
			continue;
		}

		// Invalid value — silently ignored
	}

	// If no valid values produced results, fail-open to all days.
	return result.size > 0 ? result : ALL_DAYS;
}

/**
 * Gets the day-of-week (0=Sunday..6=Saturday) for a Unix timestamp in a timezone.
 *
 * Uses `Intl.DateTimeFormat` with `weekday: 'short'` for tz-aware day resolution.
 * @param ts
 * @param tz
 */
function getDayOfWeekInTz(ts: number, tz: string): number {
	const SHORT_DAY_MAP: Record<string, number> = {
		Sun: 0,
		Mon: 1,
		Tue: 2,
		Wed: 3,
		Thu: 4,
		Fri: 5,
		Sat: 6,
	};

	try {
		const formatter = new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			weekday: 'short',
		});
		const weekday = formatter.format(new Date(ts * 1000));
		return SHORT_DAY_MAP[weekday] ?? 0;
	} catch (e) {
		// Invalid tz — fall back to UTC.
		const day = new Date(ts * 1000).getUTCDay();
		return day;
	}
}

/**
 * Counts how many days in a range match a set of target day-of-week numbers.
 *
 * Uses O(1) mathematical calculation: full weeks * matching days per week,
 * plus a remainder scan.
 *
 * @param startTs    Timestamp of the first candidate day (seconds).
 * @param totalDays  Number of candidate days in the range.
 * @param targetDays Set of day-of-week numbers (0=Sun..6=Sat) to count.
 * @param tz         IANA timezone string for tz-aware day resolution.
 */
function countMatchingDays(
	startTs: number,
	totalDays: number,
	targetDays: Set<number>,
	tz: string
): number {
	if (totalDays <= 0) {
		return 0;
	}
	if (targetDays.size === 7) {
		return totalDays;
	}

	const startDow = getDayOfWeekInTz(startTs, tz);
	const matchingPerWeek = [0, 1, 2, 3, 4, 5, 6].filter((d) =>
		targetDays.has(d)
	).length;
	const fullWeeks = Math.floor(totalDays / 7);
	const remainder = totalDays % 7;

	let count = fullWeeks * matchingPerWeek;

	for (let i = 0; i < remainder; i++) {
		const dow = (startDow + i) % 7;
		if (targetDays.has(dow)) {
			count++;
		}
	}

	return count;
}

/**
 * Resolves the `exclude` option to a flat array of DateGroupRules.
 *
 * Each entry in the exclude array is checked:
 * 1. If it's a number (field reference resolved to timestamp), treat as explicit date.
 * 2. If it matches a key in `strings.dateGroups`, that group's rules are included.
 * 3. If it matches YYYY-MM-DD format, it's treated as an explicit date rule.
 * 4. Otherwise it's silently ignored (fail-open).
 *
 * @param exclude Array of group names, date strings, or timestamps from the exclude option.
 */
function resolveExclude(
	exclude: (string | number)[] | string | number
): DateGroupRule[] {
	if (!Array.isArray(exclude)) {
		exclude = [exclude];
	}

	if (exclude.length === 0) {
		return [];
	}

	const rules: DateGroupRule[] = [];

	for (const entry of exclude) {
		// Numeric entry — a resolved field reference (UTC-midnight timestamp).
		if (typeof entry === 'number') {
			if (entry > 0) {
				rules.push({ type: 'date', ts: entry });
			}
			continue;
		}

		// Check if it's a registered date group name.
		if (strings.dateGroups && entry in strings.dateGroups) {
			rules.push(...strings.dateGroups[entry]);
			continue;
		}

		// Check if it's a wildcard pattern (contains '*').
		if (entry.includes('*')) {
			const wParts = entry.split('-');
			if (wParts.length === 3) {
				rules.push({
					type: 'wildcard',
					y: wParts[0] === '*' ? '*' : parseInt(wParts[0], 10),
					m: wParts[1] === '*' ? '*' : parseInt(wParts[1], 10),
					d: wParts[2] === '*' ? '*' : parseInt(wParts[2], 10),
				});
			}
			continue;
		}

		// Check if it's an explicit date (YYYY-MM-DD).
		const match = entry.match(/^(\d{4})-(\d{2})-(\d{2})$/);
		if (match) {
			const date = new Date(
				Date.UTC(
					parseInt(match[1], 10),
					parseInt(match[2], 10) - 1,
					parseInt(match[3], 10)
				)
			);
			// Validate the date is real (e.g. reject Feb 30).
			if (!isNaN(date.getTime())) {
				rules.push({ type: 'date', ts: date.getTime() / 1000 });
			}
			continue;
		}

		// Unrecognized — silently ignored.
	}

	return rules;
}

/**
 * Checks whether a given UTC-midnight timestamp is excluded by any rule.
 *
 * @param dateTs UTC-midnight timestamp (seconds) of the candidate day.
 * @param rules  Array of DateGroupRules to check against.
 * @param tz     IANA timezone string for decomposing the date (wildcard matching).
 */
function isDateExcluded(
	dateTs: number,
	rules: DateGroupRule[],
	tz: string
): boolean {
	let parts: DateParts | null = null;

	for (const rule of rules) {
		if (rule.type === 'date') {
			if (rule.ts === dateTs) {
				return true;
			}
			continue;
		}

		// type === 'wildcard'
		if (!parts) {
			parts = getDatePartsInTz(dateTs, tz);
		}
		if (rule.y !== '*' && rule.y !== parts.year) {
			continue;
		}
		if (rule.m !== '*' && rule.m !== parts.month) {
			continue;
		}
		if (rule.d !== '*' && rule.d !== parts.day) {
			continue;
		}
		return true;
	}

	return false;
}

/**
 * Returns the UTC offset in seconds for a timezone at a given instant.
 *
 * Uses `Intl.DateTimeFormat` with `timeZoneName: 'longOffset'` to determine
 * the timezone's UTC offset. Positive values mean east of UTC (e.g. +7200
 * for UTC+02:00), negative values mean west (e.g. -18000 for UTC-05:00).
 *
 * Returns `null` if the timezone string is invalid.
 *
 * @param ts Unix timestamp in seconds (the instant at which to evaluate the offset).
 * @param tz IANA timezone string.
 * @return   Offset in seconds east of UTC, or `null` on error.
 */
function getTzOffsetSeconds(ts: number, tz: string): number | null {
	if (tz === 'UTC') {
		return 0;
	}

	let formatter: Intl.DateTimeFormat;

	try {
		formatter = new Intl.DateTimeFormat('en-US', {
			timeZone: tz,
			timeZoneName: 'longOffset',
		});
	} catch (e) {
		return null;
	}

	const parts = formatter.formatToParts(new Date(ts * 1000));
	const tzPart = parts.find((p) => p.type === 'timeZoneName');

	if (!tzPart) {
		return null;
	}

	const gmtString = tzPart.value; // e.g. "GMT-04:00", "GMT+02:00", "GMT"

	if (gmtString === 'GMT') {
		return 0;
	}

	const match = gmtString.match(/^GMT([+-])(\d{2}):(\d{2})$/);
	if (!match) {
		return null;
	}

	const sign = match[1] === '+' ? 1 : -1;
	const hours = parseInt(match[2], 10);
	const minutes = parseInt(match[3], 10);

	return sign * (hours * 3600 + minutes * 60);
}

/**
 * Converts a date + time-of-day in a specific timezone to a UTC timestamp.
 *
 * Uses `getTzOffsetSeconds()` to determine the timezone's UTC offset at the
 * given moment and adjusts accordingly.
 *
 * If `tz` is `'UTC'`, returns `date + time` directly (no conversion).
 * If `tz` is invalid, falls back to UTC with a console warning.
 *
 * @param date UTC-midnight timestamp (seconds) representing the calendar date.
 * @param time Seconds since midnight (0–86399) representing the time-of-day.
 * @param tz   IANA timezone string.
 * @return     UTC Unix timestamp in seconds.
 */
/**
 * Parses an ISO date string ('YYYY-MM-DD') into a UTC-midnight Unix timestamp.
 * Returns NaN on invalid input.
 * @param value
 */
function parseDateString(value: string): number {
	const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
	if (!match) {
		return NaN;
	}

	// eslint-disable-next-line @wordpress/no-unused-vars-before-return
	const year = parseInt(match[1], 10);
	const month = parseInt(match[2], 10);
	const day = parseInt(match[3], 10);

	if (month < 1 || month > 12 || day < 1 || day > 31) {
		return NaN;
	}

	return Math.floor(Date.UTC(year, month - 1, day) / 1000);
}

/**
 * Parses a time string into seconds since midnight (0–86399).
 *
 * Accepts:
 *   - '12:00pm', '2:30am'  (12-hour)
 *   - '14:30'              (24-hour)
 *   - '14:30:45'           (24-hour with seconds)
 *
 * Returns NaN on invalid input.
 * @param value
 */
function parseTimeString(value: string): number {
	// HH:MM:SS (24-hour with seconds)
	let m = value.match(/^(\d{1,2}):(\d{2}):(\d{2})$/);
	if (m) {
		return (
			parseInt(m[1], 10) * 3600 +
			parseInt(m[2], 10) * 60 +
			parseInt(m[3], 10)
		);
	}

	// HH:MMam/pm (12-hour)
	m = value.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);
	if (m) {
		let hour = parseInt(m[1], 10);
		const minute = parseInt(m[2], 10);
		const period = m[3].toLowerCase();

		if (period === 'am' && hour === 12) {
			hour = 0;
		}
		if (period === 'pm' && hour !== 12) {
			hour += 12;
		}

		return hour * 3600 + minute * 60;
	}

	// HH:MM (24-hour)
	m = value.match(/^(\d{1,2}):(\d{2})$/);
	if (m) {
		return parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60;
	}

	return NaN;
}

/**
 * Converts a time string (e.g. '14:30', '2:30 pm') into a full UTC timestamp
 * for today's date in the given timezone.
 *
 * "Today" is determined by the wall-clock date in `tz`.
 *
 * Returns NaN on invalid input.
 * @param value
 * @param tz
 */
function timeStringToTimestamp(value: string, tz: string): number {
	const timeSeconds = parseTimeString(value);
	if (isNaN(timeSeconds)) {
		return NaN;
	}

	// Get today's date in the given timezone.
	// eslint-disable-next-line @typescript-eslint/no-shadow
	const now = Math.floor(Date.now() / 1000);
	const parts = getDatePartsInTz(now, tz);
	const todayMidnight = Math.floor(
		Date.UTC(parts.year, parts.month - 1, parts.day) / 1000
	);

	return toUtcTimestamp(todayMidnight, timeSeconds, tz);
}

function toUtcTimestamp(date: number, time: number, tz: string): number {
	const local = date + time;

	// First pass: approximate the offset using `local` as a proxy for UTC.
	// This can be wrong by up to ±1 hour across a DST boundary because
	// `local` represents the wall-clock time numerically, not the actual
	// UTC instant we're solving for.
	const offset1 = getTzOffsetSeconds(local, tz);

	if (offset1 === null) {
		// eslint-disable-next-line no-console
		console.warn(
			`[GPAC] datetime: invalid timezone "${tz}", falling back to UTC`
		);
		return local;
	}

	const utcGuess = local - offset1;

	// Second pass: refine by evaluating the offset at the approximate UTC
	// result. Since the maximum DST shift is ±1 hour, `utcGuess` is always
	// close enough for `getTzOffsetSeconds` to return the correct offset.
	const offset2 = getTzOffsetSeconds(utcGuess, tz);

	return local - (offset2 ?? offset1);
}

/**
 * Returns the difference between two dates.
 *
 * Both arguments are timestamps in seconds, normalized by PHP.
 * Returns the difference in days by default.
 *
 * @param start             Timestamp in seconds.
 * @param end               Timestamp in seconds.
 * @param opts
 * @param opts.unit
 * @param opts.includeStart
 * @param opts.includeEnd
 * @param opts.exclude
 * @param opts.daysOfWeek
 * @param opts.tz
 */
export function datediff(
	start: number | string,
	end: number | string,
	opts: Partial<DateDiffOpts> = {}
) {
	// Resolve string arguments to numeric timestamps.
	if (typeof start === 'string') {
		start = parseDateString(start);
	}
	if (typeof end === 'string') {
		end = parseDateString(end);
	}

	if (!start || !end || start < 0 || end < 0) {
		return 0;
	}

	const resolvedOpts: DateDiffOpts = {
		unit: 'days',
		includeStart: true,
		includeEnd: false,
		exclude: [],
		daysOfWeek: 'all',
		abs: true,
		tz: 'site',
		...opts,
	};

	if (resolvedOpts.tz === 'site') {
		resolvedOpts.tz = strings.siteTimezone;
	}

	// Normalize direction — swap so counting logic always works on a
	// positive range. Track whether a swap happened for abs=false negation.
	let swapped = false;
	if (end < start) {
		[start, end] = [end, start];
		swapped = true;
	}

	const sign = swapped && !resolvedOpts.abs ? -1 : 1;

	const diffSeconds = end - start;

	// Day-level arithmetic (days, weeks) uses UTC internally. Date field
	// values are UTC-midnight timestamps; adding DAY_SECONDS yields the
	// next UTC-midnight. Using UTC for day-of-week, exclude, and
	// countMatchingDays avoids off-by-one errors in timezones west of UTC
	// (where UTC midnight falls on the previous local calendar day).
	//
	// Calendar units (months, years) use resolvedOpts.tz so that
	// datetime() outputs with a time component are decomposed in the
	// correct timezone (the time component can push a UTC timestamp into
	// a different calendar day than the user intended).
	const internalTz = 'UTC';

	switch (resolvedOpts.unit) {
		// Day-level units: day counting with include adjustments and
		// daysOfWeek filtering.
		case 'days': {
			const targetDays = resolveDaysOfWeek(resolvedOpts.daysOfWeek);
			const excludeRules = resolveExclude(resolvedOpts.exclude);

			if (start === end) {
				if (!(resolvedOpts.includeStart && resolvedOpts.includeEnd)) {
					return 0;
				}
				// Same-day: check daysOfWeek filter and exclude.
				if (targetDays.size < 7) {
					const dow = getDayOfWeekInTz(start, internalTz);
					if (!targetDays.has(dow)) {
						return 0;
					}
				}
				if (excludeRules.length > 0) {
					if (isDateExcluded(start, excludeRules, internalTz)) {
						return 0;
					}
				}
				return sign * 1;
			}

			const dayDiff = Math.floor(diffSeconds / DAY_SECONDS);
			const totalDays = dayDiff + 1;
			const candidateDays = Math.max(
				0,
				totalDays -
					(resolvedOpts.includeStart ? 0 : 1) -
					(resolvedOpts.includeEnd ? 0 : 1)
			);

			// Fast path: no daysOfWeek filter and no exclusions.
			if (targetDays.size === 7 && excludeRules.length === 0) {
				return sign * candidateDays;
			}

			// Fast path: daysOfWeek filter only (no exclusions) — O(1) math.
			const effectiveStart = resolvedOpts.includeStart
				? start
				: start + DAY_SECONDS;

			if (excludeRules.length === 0) {
				return (
					sign *
					countMatchingDays(
						effectiveStart,
						candidateDays,
						targetDays,
						internalTz
					)
				);
			}

			// Slow path: day-by-day iteration when exclusions are present.
			let count = 0;
			for (let i = 0; i < candidateDays; i++) {
				const dayTs = effectiveStart + i * DAY_SECONDS;
				const dow = getDayOfWeekInTz(dayTs, internalTz);
				if (!targetDays.has(dow)) {
					continue;
				}
				if (isDateExcluded(dayTs, excludeRules, internalTz)) {
					continue;
				}
				count++;
			}
			return sign * count;
		}
		// Weeks: purely calendar-based (like months/years). daysOfWeek is ignored.
		case 'weeks': {
			if (start === end) {
				return 0;
			}
			const dayDiff = Math.floor(diffSeconds / DAY_SECONDS);
			const totalDays = dayDiff + 1;
			const candidateDays = Math.max(
				0,
				totalDays -
					(resolvedOpts.includeStart ? 0 : 1) -
					(resolvedOpts.includeEnd ? 0 : 1)
			);
			return sign * Math.floor(candidateDays / 7);
		}

		// Calendar units: decompose timestamps in the user's timezone so
		// that datetime() outputs with a time component map to the correct
		// calendar date (the time component can push a UTC timestamp into
		// a different calendar day than the user intended).
		case 'months': {
			const s = getDatePartsInTz(start, resolvedOpts.tz);
			const e = getDatePartsInTz(end, resolvedOpts.tz);
			let months = (e.year - s.year) * 12 + (e.month - s.month);

			if (e.day < s.day) {
				// TODO this is a bit hacky...and debatable because it basically means that
				// cases like Jan 31 - Feb 28 won't result in a full month being returned.
				// This logic needs to be hammered out and refined
				months--;
			}
			return sign * Math.max(0, months);
		}
		case 'years': {
			const s = getDatePartsInTz(start, resolvedOpts.tz);
			const e = getDatePartsInTz(end, resolvedOpts.tz);
			let years = e.year - s.year;
			const startMD = s.month * 100 + s.day;
			const endMD = e.month * 100 + e.day;
			if (endMD < startMD) {
				// TODO this is also a bit hacky...and also debatable because what actually
				// quantifies a "year"? Does it mean 365 days? Or is it like 15 Jan - 15 Jan?
				years--;
			}
			return sign * Math.max(0, years);
		}

		default:
			return sign * Math.floor(diffSeconds / DAY_SECONDS);
	}
}

/**
 * Combines a date and a time into a single datetime timestamp (UTC).
 *
 * The `tz` option declares what timezone the input date/time are in. The
 * result is always a UTC timestamp. Defaults to the WP site timezone
 * (`'site'`), matching `datediff()` behavior.
 *
 * @param date    Unix timestamp in seconds (UTC midnight) from a date field.
 *                A value of 0 (epoch) is treated as empty/missing. A value < 0
 *                signals an incomplete/unparseable date field (-1 sentinel).
 * @param time    Seconds since midnight (0–86399) from a time field.
 *                A value of 0 (midnight) is valid. A value < 0 signals an
 *                incomplete/unparseable time field (-1 sentinel).
 * @param opts
 * @param opts.tz IANA timezone string declaring the input timezone.
 *                Defaults to `'site'` (WP site timezone). Use `'UTC'` to
 *                skip timezone conversion.
 * @return Combined Unix timestamp in seconds (UTC), or 0 if either argument
 *         is missing or invalid.
 */
export function datetime(
	date: number | string,
	time: number | string,
	opts: Partial<DateTimeOpts> = {}
): number {
	// Resolve string arguments to numeric values.
	if (typeof date === 'string') {
		date = parseDateString(date);
	}
	if (typeof time === 'string') {
		time = parseTimeString(time);
	}

	if (!date || date < 0 || time === null || time < 0) {
		return 0;
	}

	let tz = opts.tz ?? 'site';
	if (tz === 'site') {
		tz = strings.siteTimezone;
	}

	return toUtcTimestamp(date, time, tz);
}

/**
 * Returns the clock-based difference between two timestamps.
 *
 * Unlike `datediff()` (calendar-based: days, weeks, months, years), `timediff()`
 * is purely arithmetic: `abs(end - start) / unitSeconds`.
 *
 * Accepts numeric timestamps or hardcoded time strings ('14:30', '2:30 pm',
 * '18:00:00'). Time strings are interpreted as today's date in the `tz`
 * timezone and converted to full UTC timestamps before computing the diff.
 *
 * By default (`dstAware=false`), returns the real elapsed UTC time between
 * the two instants. When `dstAware=true`, returns the **wall-clock**
 * difference in the specified timezone — i.e. the difference between the
 * local time representations, ignoring DST transitions.
 *
 * Example (America/New_York spring-forward, March 9 2025):
 * - `timediff(start, end)` → 1 hour (real UTC elapsed)
 * - `timediff(start, end, dstAware=true, tz='America/New_York')` → 2 hours
 *
 * @param start         Timestamp in seconds, or time string.
 * @param end           Timestamp in seconds, or time string.
 * @param opts
 * @param opts.unit     Output unit: 'seconds', 'minutes', or 'hours'. Default 'hours'.
 * @param opts.abs      Return absolute value. Default true.
 * @param opts.tz       IANA timezone string. Determines "today" for time strings and wall-clock computation when dstAware=true.
 * @param opts.dstAware When true, compute wall-clock difference instead of real elapsed time.
 */
export function timediff(
	start: number | string,
	end: number | string,
	opts: Partial<TimeDiffOpts> = {}
): number {
	// Resolve opts first — tz is needed for time string parsing.
	const resolvedOpts: TimeDiffOpts = {
		unit: 'hours',
		abs: true,
		tz: 'site',
		dstAware: false,
		...opts,
	};

	if (resolvedOpts.tz === 'site') {
		resolvedOpts.tz = strings.siteTimezone;
	}

	// Resolve string arguments to numeric timestamps.
	if (typeof start === 'string') {
		start = timeStringToTimestamp(start, resolvedOpts.tz);
	}
	if (typeof end === 'string') {
		end = timeStringToTimestamp(end, resolvedOpts.tz);
	}

	if (!start || !end || start < 0 || end < 0 || isNaN(start) || isNaN(end)) {
		return 0;
	}

	const sign = !resolvedOpts.abs && end < start ? -1 : 1;

	let diffSeconds: number;

	if (resolvedOpts.dstAware) {
		// Wall-clock mode: compute the difference between the local time
		// representations, ignoring DST offset changes.
		const sp = getDatePartsInTz(start, resolvedOpts.tz);
		const ep = getDatePartsInTz(end, resolvedOpts.tz);

		const startNaive =
			Date.UTC(
				sp.year,
				sp.month - 1,
				sp.day,
				sp.hour,
				sp.minute,
				sp.second
			) / 1000;
		const endNaive =
			Date.UTC(
				ep.year,
				ep.month - 1,
				ep.day,
				ep.hour,
				ep.minute,
				ep.second
			) / 1000;

		diffSeconds = Math.abs(endNaive - startNaive);
	} else {
		// Default: real elapsed UTC time.
		diffSeconds = Math.abs(end - start);
	}

	switch (resolvedOpts.unit) {
		case 'seconds':
			return sign * diffSeconds;
		case 'minutes':
			return sign * (diffSeconds / MINUTE_SECONDS);
		case 'hours':
			return sign * (diffSeconds / HOUR_SECONDS);
		default:
			return sign * (diffSeconds / HOUR_SECONDS);
	}
}

/**
 * Returns the current date as a UTC-midnight timestamp in seconds.
 *
 * Uses the site timezone to determine what "today" is, then returns the
 * UTC-midnight timestamp for that calendar date. This matches the format
 * that date fields produce, so `datediff(F1, today())` works directly.
 *
 * Takes no arguments.
 */
export function today(): number {
	const tz = strings.siteTimezone;
	const nowTs = Math.floor(Date.now() / 1000);
	const parts = getDatePartsInTz(nowTs, tz);
	return Date.UTC(parts.year, parts.month - 1, parts.day) / 1000;
}

/**
 * Returns the current UTC timestamp in seconds.
 *
 * This is the full timestamp including time-of-day, suitable for use with
 * `timediff()`. For example: `timediff(datetime(F1, F5, tz='UTC'), now())`
 *
 * Takes no arguments.
 */
export function now(): number {
	return Math.floor(Date.now() / 1000);
}

/**
 * Convenience alias for `datediff()` with `daysOfWeek='weekdays'` and `unit='days'`.
 *
 * Counts only Monday–Friday between two dates.
 *
 * @param start Timestamp in seconds.
 * @param end   Timestamp in seconds.
 * @param opts  Named arguments (same as datediff, minus unit and daysOfWeek).
 */
export function weekdays(
	start: number,
	end: number,
	opts: Partial<DateDiffOpts> = {}
): number {
	return datediff(start, end, {
		...opts,
		unit: 'days',
		daysOfWeek: 'weekdays',
	});
}

/**
 * Convenience alias for `datediff()` with `daysOfWeek='weekends'` and `unit='days'`.
 *
 * Counts only Saturday–Sunday between two dates.
 *
 * @param start Timestamp in seconds.
 * @param end   Timestamp in seconds.
 * @param opts  Named arguments (same as datediff, minus unit and daysOfWeek).
 */
export function weekenddays(
	start: number,
	end: number,
	opts: Partial<DateDiffOpts> = {}
): number {
	return datediff(start, end, {
		...opts,
		unit: 'days',
		daysOfWeek: 'weekends',
	});
}

/**
 * Returns the age (in complete years by default) from a date to today.
 *
 * Convenience alias for `datediff(date, today(), unit='years', ...opts)`.
 * The `unit` defaults to `'years'` but can be overridden (e.g. `unit='months'`).
 *
 * @param date Timestamp in seconds (e.g. a birthdate from a date field).
 * @param opts Named arguments (same as datediff). Default unit is 'years'.
 */
export function age(date: number, opts: Partial<DateDiffOpts> = {}): number {
	return datediff(date, today(), { unit: 'years', abs: false, ...opts });
}

/**
 * Converts a timestamp between timezones.
 *
 * Interprets `value` as a local time in `fromTz` (default: site timezone),
 * converts it to the true UTC instant, then re-expresses that instant as
 * local time in `toTz`. The returned number is a "fake UTC" timestamp that,
 * when fed into other GPAC functions, behaves as if the moment occurred in
 * the target timezone.
 *
 * @param value       Unix timestamp in seconds (e.g. from a date field, `datetime()`, `now()`).
 * @param toTz        Target timezone. IANA string (e.g. `'UTC'`, `'America/New_York'`).
 * @param opts
 * @param opts.fromTz Source timezone. Defaults to `'site'` (WP site timezone).
 * @return Adjusted timestamp in seconds, or 0 if `value` is missing/invalid.
 */
export function converttz(
	value: number,
	toTz: string,
	opts: Partial<ConvertTzOpts> = {}
): number {
	if (!value || value < 0) {
		return 0;
	}

	let fromTz = opts.fromTz ?? 'site';
	if (fromTz === 'site') {
		fromTz = strings.siteTimezone;
	}

	if (toTz === 'site') {
		toTz = strings.siteTimezone;
	}

	// No conversion needed if source and target are the same.
	if (fromTz === toTz) {
		return value;
	}

	// Step 1: Interpret value as local time in fromTz → find the UTC instant.
	// Two-pass approach: the offset at `value` (a pseudo-local timestamp)
	// can be wrong by up to ±1 hour near a DST boundary. Refine by
	// evaluating the offset at the approximate UTC result.
	const fromOffset1 = getTzOffsetSeconds(value, fromTz);
	if (fromOffset1 === null) {
		// eslint-disable-next-line no-console
		console.warn(
			`[GPAC] converttz: invalid fromTz "${fromTz}", returning value unchanged`
		);
		return value;
	}
	const utcGuess = value - fromOffset1;
	const fromOffset2 = getTzOffsetSeconds(utcGuess, fromTz);
	const utcInstant = value - (fromOffset2 ?? fromOffset1);

	// Step 2: Express the UTC instant as local time in toTz.
	const toOffset = getTzOffsetSeconds(utcInstant, toTz);
	if (toOffset === null) {
		// eslint-disable-next-line no-console
		console.warn(
			`[GPAC] converttz: invalid toTz "${toTz}", returning value unchanged`
		);
		return value;
	}

	return utcInstant + toOffset;
}
